import { CobaltActionBar } from '@cae/cobalt-react/components/action-bar';
import { CobaltButton } from '@cae/cobalt-react/components/button';
import { CobaltSidePanel } from '@cae/cobalt-react/components/side-panel';
import { useSubmitDocumentForm } from '@features/documents/hooks/use-submit-document-form/useSubmitDocumentForm';
import { useSubmitDocument } from '@features/documents/hooks/use-submit-document/useSubmitDocument';
import { UnsavedChangesModal } from '@shared/components/unsaved-changes-modal/UnsavedChangesModal';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import type { SubmitDocumentPanelProps } from './SubmitDocumentPanel.types';

import { SubmitDocumentForm } from './components/submit-document-form/SubmitDocumentForm';
import { SubmitDocumentMessages } from './components/submit-document-messages/SubmitDocumentMessages';
import styles from './SubmitDocumentPanel.module.css';

const SubmitDocumentPanel = React.memo(
  ({
    isOpen,
    onClose,
    organizationId,
    submittedDocumentsPath,
  }: Readonly<SubmitDocumentPanelProps>): React.ReactElement | null => {
    const navigate = useNavigate();
    const mutation = useSubmitDocument(organizationId);

    const {
      formState,
      uploaderFiles,
      showValidation,
      showDiscardModal,
      isSubmitting,
      isSuccess,
      isError,
      uploadResponse,
      handleAuthorityChange,
      handleFilesChange,
      handleNotesChange,
      handleSubmit,
      handleClose,
      handleCloseAttempt,
      handleCancelDiscard,
      handleSubmitMore,
    } = useSubmitDocumentForm({
      isPending: mutation.isPending,
      isSuccess: mutation.isSuccess,
      isError: mutation.isError,
      mutate: mutation.mutate,
      reset: mutation.reset,
      onClose,
    });

    const handleNavigateToSubmitted = React.useCallback((): void => {
      handleClose();
      navigate(submittedDocumentsPath);
    }, [handleClose, navigate, submittedDocumentsPath]);

    if (!isOpen) return null;

    return (
      <>
        <CobaltSidePanel
          isOpen={isOpen}
          title="Submit document"
          onClose={handleCloseAttempt}
          showCloseButton
          actionBar={
            isSuccess ? (
              <CobaltActionBar
                position="right"
                variant="floating"
                primaryButton={
                  <CobaltButton variant="secondary" onClick={handleClose}>
                    Done
                  </CobaltButton>
                }
              />
            ) : (
              <CobaltActionBar
                position="right"
                variant="floating"
                primaryButton={
                  <CobaltButton
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    pending={isSubmitting}
                  >
                    Submit
                  </CobaltButton>
                }
                secondaryButton={
                  <CobaltButton
                    variant="secondary"
                    onClick={handleCloseAttempt}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </CobaltButton>
                }
              />
            )
          }
        >
          <div className={styles.submitDocumentPanel}>
            <SubmitDocumentMessages
              isSuccess={isSuccess}
              isError={isError}
              onNavigateToSubmitted={handleNavigateToSubmitted}
              onSubmitMore={handleSubmitMore}
            />

            <SubmitDocumentForm
              formState={formState}
              uploaderFiles={uploaderFiles}
              showValidation={showValidation}
              isSubmitting={isSubmitting}
              isSuccess={isSuccess}
              uploadResponse={uploadResponse}
              onAuthorityChange={handleAuthorityChange}
              onFilesChange={handleFilesChange}
              onNotesChange={handleNotesChange}
            />
          </div>
        </CobaltSidePanel>

        <UnsavedChangesModal
          isOpen={showDiscardModal}
          onConfirm={handleClose}
          onCancel={handleCancelDiscard}
        />
      </>
    );
  }
);

SubmitDocumentPanel.displayName = 'SubmitDocumentPanel';

export { SubmitDocumentPanel };
